import { inject, Injectable } from '@angular/core';

import { UserStateService } from 'src/app/auth/services/user-state.service';
import { ENTITY_TYPES, EntityType } from 'src/app/auth/types/entity-type';
import { ModalService } from 'src/app/components/modals/modal.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { type ListItem } from 'src/app/components/scrolling-list/services';
import {
  ShareAccessLevel,
  ShareWithUsersModalComponent,
} from 'src/app/components/share-with-users-modal/share-with-users-modal.component';

type FolderShareLevels = {
  accessLevels: readonly ShareAccessLevel[];
  defaultAccessLevel: ShareAccessLevel;
};

/** A Folder is shared with the same levels as the items it holds. */
const FOLDER_SHARE_LEVELS: Record<EntityType, FolderShareLevels> = {
  [ENTITY_TYPES.PRESENTATION]: {
    accessLevels: ['Editor', 'Publisher', 'Full Access'],
    defaultAccessLevel: 'Editor',
  },
  [ENTITY_TYPES.SCHEDULE]: {
    accessLevels: ['Playlist Manager', 'Full Access'],
    defaultAccessLevel: 'Playlist Manager',
  },
  [ENTITY_TYPES.DISPLAY]: {
    accessLevels: ['Screen Sharing Moderator', 'Full Access'],
    defaultAccessLevel: 'Screen Sharing Moderator',
  },
};

/** Sharing Folders with other users. */
@Injectable({
  providedIn: 'root',
})
export class FolderSharingService {
  private readonly userStateService = inject(UserStateService);
  private readonly featuresService = inject(FeaturesService);
  private readonly modalService = inject(ModalService);

  /**
   * Folder sharing is released incrementally. Until it ships, it is only
   * available outside production and beta.
   */
  isReleased(): boolean {
    return this.userStateService.hasPermission('!PROD !BETA');
  }

  hasFolder<T extends { folderId?: string }>(
    selected?: ListItem<T>[],
  ): boolean {
    return !!selected?.some((listItem) => listItem.isFolder);
  }

  /**
   * Until folder sharing is released, "Share with others" stays hidden for any
   * selection with a Folder, as it was before.
   */
  isShareHidden<T extends { folderId?: string }>(
    selected?: ListItem<T>[],
  ): boolean {
    return !this.isReleased() && this.hasFolder(selected);
  }

  /** Whether a selection is shared through the Folder share dialog. */
  sharesFolders<T extends { folderId?: string }>(
    selected?: ListItem<T>[],
  ): boolean {
    return this.isReleased() && this.hasFolder(selected);
  }

  /**
   * Opens the share dialog for a selection with one or more Folders, and any
   * items selected with them. Folders and items of one list share the same
   * levels. Saving is not connected yet, so the dialog closes without changing
   * who can access the selection.
   */
  async shareFolders(entityType: EntityType): Promise<unknown> {
    await this.featuresService.confirmShareWithOthersAvailable();

    return this.modalService.showMediumDialog(
      ShareWithUsersModalComponent,
      {
        accessControlList: {},
        ...FOLDER_SHARE_LEVELS[entityType],
      },
      { width: '640px', maxWidth: 'calc(100vw - 32px)' },
    );
  }
}
